import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate, useLocation } from 'react-router';
import env from '@jwp/ott-common/src/env';
import {
  GET_MEDIA_APPROVAL_BY_ID,
  GetMediaApprovalByIdData,
  GetMediaApprovalByIdVariables,
  MaAdditionalImage,
} from '@jwp/ott-common/src/graphql/query/moderate.queries';
import {
  APPROVE_OR_REJECT_MEDIA_APPROVAL,
  ApproveOrRejectMediaApprovalData,
  ApproveOrRejectMediaApprovalVariables,
} from '@jwp/ott-common/src/graphql/mutation/moderate.mutation';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { PATH_VIDEO_PREVIEW, PATH_VIDEO_PREVIEW_WITH_ID } from '@jwp/ott-common/src/paths';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';

import Button from '../Button/Button';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import UploadBox from '../UploadBox/UploadBox';

import styles from './VideoReviewModal.module.scss';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

type Props = {
  maId: string;
  viewOnly?: boolean;
};

const VideoReviewModal: React.FC<Props> = ({ maId, viewOnly = false }) => {
  const { t } = useTranslation(['video', 'common', 'upload']);
  const navigate = useNavigate();
  const location = useLocation();
  const { setReturnPath, setPreviewUrl, setVideoValues } = useUploadStore();

  const { data, loading, error } = useQuery<GetMediaApprovalByIdData, GetMediaApprovalByIdVariables>(GET_MEDIA_APPROVAL_BY_ID, {
    variables: { maId },
    skip: !maId,
  });

  const [approveOrReject, { loading: mutationLoading }] = useMutation<ApproveOrRejectMediaApprovalData, ApproveOrRejectMediaApprovalVariables>(
    APPROVE_OR_REJECT_MEDIA_APPROVAL,
    {
      onCompleted: () => {
        navigate(createURLFromLocation(location, { u: 'approval-success-video', id: null }));
      },
      refetchQueries: ['getAllMediaApprovalsList'],
    },
  );

  const handleApprove = () => {
    approveOrReject({
      variables: {
        input: {
          maId,
          maStatus: 'APPROVED',
        },
      },
    });
  };

  const handleReject = () => {
    navigate(createURLFromLocation(location, { u: 'reject-reason', message: 'video' }));
  };

  const media = data?.mediaApprovalByMaId;
  const videoUrl = media ? env.APP_BASE_URL + media.maS3MediaKeyUrl : '';
  const posterUrl = media ? env.APP_BASE_URL + media.maS3ThumbnailKeyUrl : '';

  const additionalImages: MaAdditionalImage[] = React.useMemo(() => {
    const raw = media?.maAdditionalImages as any;
    if (!raw) { return []; }

    try {
      if (Array.isArray(raw)) {
        // Handle potential double-serialization like in tags (array of one stringified JSON array)
        if (raw.length === 1 && typeof raw[0] === 'string' && raw[0].startsWith('[')) {
          const parsed = JSON.parse(raw[0]);
          return Array.isArray(parsed) ? parsed : [];
        }
        // If it's an array of strings (serialized objects)
        if (typeof raw[0] === 'string') {
          return raw.map((s: string) => {
            try {
              return JSON.parse(s);
            } catch {
              return null;
            }
          }).filter(Boolean);
        }
        return raw;
      }

      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [parsed];
      }
    } catch (e) {
      console.error('Failed to parse maAdditionalImages:', e);
    }

    return [];
  }, [media?.maAdditionalImages]);

  const motionThumbnail = additionalImages.find(img => img.maAdditionalImagesLabelValue === 'video');
  const staticThumbnail = additionalImages.find(img => img.maAdditionalImagesLabelValue === 'static');
  const otherImages = additionalImages.filter(img =>
    img.maAdditionalImagesLabelValue !== 'video' && img.maAdditionalImagesLabelValue !== 'static'
  );


  const isAlreadyUpdated =
    media?.maStatus === 'APPROVED' ||
    media?.maStatus === 'REJECTED';

  const renderModalContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingWrapper}>
          <LoadingOverlay inline />
        </div>
      );
    }

    if (error || !media) {
      return <div className={styles.errorWrapper}>{t('common:error_loading_data')}</div>;
    }

    const genres = media.genreDropdownsByMaId?.nodes?.map(n => n.dropdownByDdId.ddLabel) || [];
    const ageRating = media.dropdownByAgeRatingId?.ddLabel || '-';

    return (
      <div className={styles.mainWrapper}>
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            <div className={styles.section}>
              <span className={styles.sectionLabel}>{t('upload:upload_video.form.video_label')}</span>
              <UploadBox
                selectedFile={null}
                onFileSelect={() => { }}
                poster={posterUrl}
                previewUrl={videoUrl}
                type="video"
                height={250}
                disabled={true}
                duration={media.maDuration ? Number(media.maDuration) : null}
                onPreview={() => {
                  const backPath = `${location.pathname}${location.search}`;
                  setReturnPath(backPath);
                  setPreviewUrl(videoUrl);
                  setVideoValues({
                    title: media.maTitle,
                    description: media.maDescription,
                    tags: Array.isArray(media.maTags) ? media.maTags.join(', ') : '',
                  });
                  navigate(`${PATH_VIDEO_PREVIEW}?type=video&returnPath=${encodeURIComponent(backPath)}`);
                }}
              />
            </div>

            <div className={styles.section}>
              <span className={styles.sectionLabel}>{t('upload:upload_video.form.thumbnails_label')}</span>
              <div className={styles.thumbnailsRow}>
                <div className={styles.thumbItem}>
                  <span className={styles.subLabel}>{t('upload:upload_video.form.motion_thumbnail_label')}</span>
                  <UploadBox
                    selectedFile={null}
                    onFileSelect={() => { }}
                    poster={motionThumbnail ? env.APP_BASE_URL + motionThumbnail.maAdditionalImagesS3KeyUrl : ''}
                    previewUrl={motionThumbnail ? env.APP_BASE_URL + motionThumbnail.maAdditionalImagesS3KeyUrl : ''}
                    type="video"
                    height={119}
                    disabled={true}
                    autoPlay={true}
                    showVideoControls={false}
                  />
                </div>
                <div className={styles.thumbItem}>
                  <span className={styles.subLabel}>{t('upload:upload_video.form.static_thumbnail_label')}</span>
                  <UploadBox
                    selectedFile={null}
                    onFileSelect={() => { }}
                    poster={staticThumbnail ? env.APP_BASE_URL + staticThumbnail.maAdditionalImagesS3KeyUrl : posterUrl}
                    previewUrl={staticThumbnail ? env.APP_BASE_URL + staticThumbnail.maAdditionalImagesS3KeyUrl : posterUrl}
                    type="image"
                    height={119}
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            {otherImages.length > 0 && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>{t('upload:upload_video.form.additional_images_label')}</span>
                <div className={styles.additionalGallery}>
                  {otherImages.map((img, idx) => (
                    <div key={idx} className={styles.galleryItem}>
                      <UploadBox
                        selectedFile={null}
                        onFileSelect={() => { }}
                        poster={env.APP_BASE_URL + img.maAdditionalImagesS3KeyUrl}
                        previewUrl={env.APP_BASE_URL + img.maAdditionalImagesS3KeyUrl}
                        type="image"
                        height={140}
                        disabled={true}
                        label={img.maAdditionalImagesLabelName}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.title_label')}</span>
              <p className={styles.value}>{media.maTitle || '-'}</p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.description_label')}</span>
              <p className={styles.value}>{media.maDescription || '-'}</p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.genre_label')}</span>
              <div className={styles.chipWrapper}>
                {genres.length > 0 ? genres.map((g, idx) => (
                  <span key={idx} className={styles.chip}>{g}</span>
                )) : '-'}
              </div>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.age_rating_label')}</span>
              <p className={styles.value}>{ageRating}</p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.tags_label')}</span>
              <p className={styles.tagsValue}>
                {(() => {
                  const rawTags = media.maTags || [];
                  let tags = typeof rawTags === 'string' ? [rawTags] : rawTags;

                  // Handle double-serialized JSON strings within the array
                  if (tags.length === 1 && typeof tags[0] === 'string' && tags[0].startsWith('[')) {
                    try {
                      const parsed = JSON.parse(tags[0]);
                      if (Array.isArray(parsed)) {
                        tags = parsed;
                      }
                    } catch (e) {
                      console.error('Failed to parse tags:', e);
                    }
                  }

                  return tags.length > 0 ? tags.map((tag) => (tag.trim().startsWith('#') ? tag.trim() : `#${tag.trim()}`)).join(', ') : '-';
                })()}
              </p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:video_review.form.creator_label')}</span>
              <div className={styles.creatorRow}>
                <img src={`https://ui-avatars.com/api/?name=${media.userByMaUploadedBy?.uFullName || 'User'}&background=random`} alt="Creator" className={styles.avatar} />
                <span className={styles.creatorName}>{media.userByMaUploadedBy?.uFullName || '-'}</span>
              </div>
            </div>

            {(viewOnly || isAlreadyUpdated) && media.maStatus && (
              <div className={styles.infoGroup}>
                <span className={styles.label}>{t('upload:reel_review.form.status_label', { defaultValue: 'Status' })}</span>
                <p>{media.maStatus}</p>
              </div>
            )}

            {(viewOnly || isAlreadyUpdated) && media.maStatus === 'REJECTED' && media.maRejectionReason && (
              <div className={styles.infoGroup}>
                <span className={styles.label}>{t('upload:rejection.form.description_label')}</span>
                <p>{media.maRejectionReason}</p>
              </div>
            )}
          </div>
        </div>

        {!viewOnly && !isAlreadyUpdated && (
          <div className={styles.actions}>
            <Button
              label={t('upload:video_review.form.reject_button')}
              variant="outlined"
              color="delete"
              className={styles.rejectBtn}
              onClick={handleReject}
              disabled={mutationLoading}
            />
            <Button
              label={t('upload:video_review.form.approve_button')}
              color="primary"
              className={styles.approveBtn}
              onClick={handleApprove}
              busy={mutationLoading}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{viewOnly ? t('upload:video_review.details_title') : t('upload:video_review.title')}</h2>
      {renderModalContent()}
    </div>
  );
};

export default VideoReviewModal;
